import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

/**
 * Validates that a string is a valid HTTP or HTTPS URL.
 * Returns null if the control is empty or valid.
 * Rejects javascript: scheme and other non-http(s) protocols.
 * Accepts intranet URLs without TLD.
 */
export function urlValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = String(control.value ?? '').trim();
    if (!value) return null;

    // Disallow whitespace anywhere in the URL.
    if (/\s/.test(value)) {
      return { url: true };
    }

    // Reject javascript: and data: schemes immediately
    if (/^(javascript|data|vbscript|file):/i.test(value)) {
      return { url: true };
    }

    // Only treat it as having a scheme if it uses the common "scheme://" form.
    // This avoids mis-classifying host:port (e.g. "localhost:4200") as a scheme.
    const hasScheme = /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(value);
    const candidate = hasScheme
      ? value
      : value.startsWith('//')
        ? `https:${value}`
        : `https://${value}`;

    try {
      const url = new URL(candidate);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return { url: true };
      }
      return null;
    } catch {
      return { url: true };
    }
  };
}

/**
 * Validates URL but allows empty values.
 * Useful for optional URL fields like "Support Link".
 */
export function linkOrEmptyValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = String(control.value ?? '').trim();
    if (!value) return null;
    return urlValidator()(control);
  };
}

/**
 * Validates URL but also allows the Company Portal launch syntax.
 * Example: companyportal:*
 */
export function companyPortalOrHttpUrlValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = String(control.value ?? '').trim();
    if (!value) return null;

    // Disallow whitespace anywhere.
    if (/\s/.test(value)) {
      return { url: true };
    }

    // Allow Company Portal launch syntax.
    if (/^companyportal:/i.test(value)) {
      const rest = value.slice('companyportal:'.length);
      return rest.trim() ? null : { url: true };
    }

    return urlValidator()(control);
  };
}

/**
 * Validates that an array control has at least one item.
 */
export function requireAtLeastOneValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!Array.isArray(value) || value.length === 0) {
      return { required: true };
    }
    return null;
  };
}

/**
 * Validates an email but allows empty values.
 */
export function optionalEmailValidator(): ValidatorFn {
  return (control: AbstractControl) => {
    const value = String(control.value ?? '');
    if (!value) return null;
    return Validators.email(control);
  };
}

/**
 * Returns a validator that requires the control when any field control has text.
 * Automatically revalidates when the field value changes.
 */
export function requiredIfFieldHasText(fieldNames: string | string[], errorKey = 'required'): ValidatorFn {
  const names = Array.isArray(fieldNames) ? fieldNames : [fieldNames];
  let subscribed = false;

  return (control: AbstractControl): ValidationErrors | null => {
    const parent = control.parent as FormGroup | null;
    if (!parent) return null;

    if (!subscribed) {
      subscribed = true;
      for (const name of names) {
        parent.get(name)?.valueChanges.subscribe(() => {
          control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        });
      }
    }

    const anyFieldHasText = names.some(
      (name) => String(parent.get(name)?.value ?? '').length > 0,
    );
    if (!anyFieldHasText) return null;

    return String(control.value ?? '').trim() ? null : { [errorKey]: true };
  };
}
